import React, { FC, useCallback, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { isString, map, sum } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { AddExceptionModal } from 'components/Overlay';
import { errorToast, successToast } from 'components/Toast';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { selectCompliance } from 'containers/Compliance/selectors';
import { actions } from 'containers/Compliance/slice';
import { ExceptionFor } from 'containers/Findings/FindingsOverview/types';
import { actions as sharedStateAction } from 'containers/SharedState/slice';

interface IAddException {
  exceptionFor: ExceptionFor;
  policy?: Record<string, any>;
  resource?: Record<string, any>;
  onClose();
  onSuccess?();
  isOpen: boolean;
}

export const ComplianceAddException: FC<IAddException> = props => {
  const { policy, onClose, isOpen, onSuccess, exceptionFor, resource } = props;
  const { selectedBlueprint, environment } = useSelector(selectCompliance);
  const [exceptionDescription, setExceptionDescription] = useState('');
  const [exceptionApplyTime, setExceptionApplyTime] = useState({
    label: '6 months',
    value: dayjs().utc().add(6, 'months').format(),
  });
  const [selectedEnvironments, setSelectedEnvironments] = useState<
    Record<string, any>[]
  >([]);
  const dispatch = useDispatch();
  const { getCloudNativeName } = useResourceType();
  const [accounts, setAccounts] = useState<number>(0);
  const { environmentMapping } = useEnvironmentOptions();

  useEffect(() => {
    setAccounts(
      sum(
        map(
          selectedBlueprint?.environments,
          o => environmentMapping?.[o].account_ids.length,
        ),
      ),
    );
  }, [selectedBlueprint, environmentMapping]);

  const onClickAddException = useCallback(() => {
    if (exceptionFor === 'PolicyResource')
      dispatch(
        sharedStateAction.addException({
          q: {
            blueprintExceptionCreate: {
              blueprint_ids: [selectedBlueprint?.id],
              rule_ids: [policy?.rule_id],
              native_resource: resource?.type,
              resource_ids: resource?.cloud_resource_id,
              environment_id: environment?.id,
              description: exceptionDescription,
              expire_at: exceptionApplyTime.value,
            },
          },
          onSuccess: res => {
            onSuccess?.();
            onClose();
            if (isString(res)) {
              successToast({ title: res });
            } else {
              successToast({
                title: `Successfully added exception!`,
              });
            }
          },
          onError: error =>
            errorToast({
              title: `Failed to add exception.`,
              description: error.description,
            }),
        }),
      );
    else
      dispatch(
        actions.addException({
          q: {
            blueprintId: selectedBlueprint?.id,
            ruleInstanceMultiCreate: {
              rules: [policy?.rule_id],
            },
          },
          onSuccess: () => {
            onSuccess?.();
            onClose();
          },
        }),
      );
  }, [
    dispatch,
    policy,
    selectedBlueprint,
    onClose,
    onSuccess,
    environment,
    exceptionApplyTime,
    exceptionDescription,
    resource,
    exceptionFor,
  ]);

  return (
    <AddExceptionModal
      description={policy?.description}
      onConfirm={() => onClickAddException()}
      isOpen={isOpen}
      onClose={onClose}
      resourceType={policy?.native_resource}
      selectedBlueprint={selectedBlueprint}
      exceptionDescription={exceptionDescription}
      setExceptionDescription={setExceptionDescription}
      setExceptionApplyTime={setExceptionApplyTime}
      exceptionApplyTime={exceptionApplyTime}
      exceptionFor={exceptionFor}
      rule={policy}
      environment={environment?.id}
      accountId={resource?.account_id}
      subHeader={
        !!resource
          ? resource?.name + ' / ' + resource?.cloud_resource_id
          : policy?.check_stats?.total_entities +
            ' ' +
            getCloudNativeName(policy?.native_resource) +
            ' in ' +
            selectedBlueprint?.environments?.length +
            ' environment' +
            (selectedBlueprint?.environments?.length === 1 ? ', ' : 's, ') +
            accounts +
            ' account' +
            (accounts === 1 ? '' : 's')
      }
      selectedEnvironments={selectedEnvironments}
      setSelectedEnvironments={setSelectedEnvironments}
    />
  );
};
